import { useState, useEffect } from "react";
import { Form, useLoaderData, useActionData, useNavigation, useFetcher } from "react-router-dom";
import toast from "react-hot-toast";
import { Header } from "../../components/layout/Header";
import { Button, LinkButton } from "../../components/common";
import { useAuth } from "../../hooks/useAuth";
import { PODCAST_CATEGORIES, PODCAST_LANGUAGES } from "../../schemas";
import type { PodcastDetailData } from "../../routes/loaders";
import type { ActionResult } from "../../routes/actions";
import { CustomDomainSection } from "../../components/domains/CustomDomainSection";
import { TeamAccessSection } from "../../components/teams/TeamAccessSection";
import { PricingManagement } from "../../components/pricing/PricingManagement";
import styles from "./PodcastSettingsPage.module.css";

// Common timezones
const TIMEZONES = [
  { value: "UTC", label: "UTC" },
  { value: "America/New_York", label: "Eastern Time (US)" },
  { value: "America/Chicago", label: "Central Time (US)" },
  { value: "America/Denver", label: "Mountain Time (US)" },
  { value: "America/Los_Angeles", label: "Pacific Time (US)" },
  { value: "Europe/London", label: "London" },
  { value: "Europe/Paris", label: "Paris" },
  { value: "Europe/Berlin", label: "Berlin" },
  { value: "Asia/Tokyo", label: "Tokyo" },
  { value: "Asia/Shanghai", label: "Shanghai" },
  { value: "Australia/Sydney", label: "Sydney" },
];

export default function PodcastSettingsPage() {
  const { podcast, organizationId } = useLoaderData() as PodcastDetailData;
  const actionData = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();
  const deleteFetcher = useFetcher();
  const { user } = useAuth();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isSubmitting = navigation.state === "submitting";
  const isDeleting = deleteFetcher.state === "submitting";

  const userOrg = user?.organizations.find((o) => o.id === organizationId);
  const userRole = userOrg?.role || null;
  const canDelete = userRole === "owner" || userRole === "admin";

  useEffect(() => {
    if (!actionData) return;

    if (actionData.success) {
      toast.success("Podcast settings updated successfully!");
    } else if (actionData.error) {
      toast.error(actionData.error);
    }
  }, [actionData]);

  useEffect(() => {
    if (deleteFetcher.data) {
      const result = deleteFetcher.data as ActionResult;
      if (result.error) {
        toast.error(result.error);
        setShowDeleteConfirm(false);
      }
    }
  }, [deleteFetcher.data]);

  return (
    <>
      <Header />
      <div className="container">
        <div className={styles.container}>
          <div className={styles.header}>
            <h1>Podcast Settings</h1>
            <LinkButton
              to={`/organizations/${organizationId}/podcasts/${podcast.id}`}
              variant="secondary"
            >
              Back to Podcast
            </LinkButton>
          </div>

          {/* General Settings */}
          <div className={styles.section}>
            <h2>General Settings</h2>
            <Form method="post" className={styles.form}>
              <input type="hidden" name="_section" value="general" />
              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.name ? styles.formGroupError : ""}`}
              >
                <label htmlFor="name">Podcast Name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  defaultValue={podcast.name}
                  placeholder="My Awesome Podcast"
                  disabled={isSubmitting}
                />
                {actionData?.fieldErrors?.name && (
                  <span className={styles.formError}>{actionData.fieldErrors.name}</span>
                )}
              </div>

              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.slug ? styles.formGroupError : ""}`}
              >
                <label htmlFor="slug">URL Slug</label>
                <input
                  id="slug"
                  name="slug"
                  type="text"
                  defaultValue={podcast.slug}
                  placeholder="my-awesome-podcast"
                  disabled={isSubmitting}
                />
                {actionData?.fieldErrors?.slug && (
                  <span className={styles.formError}>{actionData.fieldErrors.slug}</span>
                )}
                <small className={styles.helpText}>
                  Only lowercase letters, numbers, and hyphens allowed. Used in your RSS feed URL.
                </small>
              </div>

              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.description ? styles.formGroupError : ""}`}
              >
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  defaultValue={podcast.description || ""}
                  placeholder="Tell listeners what your podcast is about..."
                  rows={4}
                  disabled={isSubmitting}
                />
                {actionData?.fieldErrors?.description && (
                  <span className={styles.formError}>{actionData.fieldErrors.description}</span>
                )}
              </div>

              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.coverArtworkUrl ? styles.formGroupError : ""}`}
              >
                <label htmlFor="coverArtworkUrl">Cover Artwork URL</label>
                <div className={styles.artworkField}>
                  {podcast.coverArtworkUrl && (
                    <img
                      src={podcast.coverArtworkUrl}
                      alt="Current artwork"
                      className={styles.artworkPreview}
                    />
                  )}
                  <input
                    id="coverArtworkUrl"
                    name="coverArtworkUrl"
                    type="url"
                    defaultValue={podcast.coverArtworkUrl || ""}
                    placeholder="https://example.com/artwork.jpg"
                    disabled={isSubmitting}
                  />
                </div>
                {actionData?.fieldErrors?.coverArtworkUrl && (
                  <span className={styles.formError}>{actionData.fieldErrors.coverArtworkUrl}</span>
                )}
                <small className={styles.helpText}>Recommended: 3000x3000px, square image.</small>
              </div>

              <div className={styles.twoColumn}>
                <div
                  className={`${styles.formGroup} ${actionData?.fieldErrors?.language ? styles.formGroupError : ""}`}
                >
                  <label htmlFor="language">Language</label>
                  <select
                    id="language"
                    name="language"
                    defaultValue={podcast.language}
                    disabled={isSubmitting}
                  >
                    {PODCAST_LANGUAGES.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.name}
                      </option>
                    ))}
                  </select>
                  {actionData?.fieldErrors?.language && (
                    <span className={styles.formError}>{actionData.fieldErrors.language}</span>
                  )}
                </div>

                <div
                  className={`${styles.formGroup} ${actionData?.fieldErrors?.category ? styles.formGroupError : ""}`}
                >
                  <label htmlFor="category">Category</label>
                  <select
                    id="category"
                    name="category"
                    defaultValue={podcast.category || ""}
                    disabled={isSubmitting}
                  >
                    <option value="">Select a category</option>
                    {PODCAST_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                  {actionData?.fieldErrors?.category && (
                    <span className={styles.formError}>{actionData.fieldErrors.category}</span>
                  )}
                </div>
              </div>

              <div className={styles.twoColumn}>
                <div
                  className={`${styles.formGroup} ${actionData?.fieldErrors?.timezone ? styles.formGroupError : ""}`}
                >
                  <label htmlFor="timezone">Timezone</label>
                  <select
                    id="timezone"
                    name="timezone"
                    defaultValue={podcast.timezone}
                    disabled={isSubmitting}
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                  {actionData?.fieldErrors?.timezone && (
                    <span className={styles.formError}>{actionData.fieldErrors.timezone}</span>
                  )}
                  <small className={styles.helpText}>Used for scheduled episode publishing</small>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="explicit"
                      value="true"
                      defaultChecked={podcast.explicit}
                      disabled={isSubmitting}
                    />
                    <span>Contains explicit content</span>
                  </label>
                  <small className={styles.helpText}>
                    Mark if your podcast contains adult language or themes
                  </small>
                </div>
              </div>

              <div className={styles.formActions}>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting}
                  loadingText="Saving..."
                >
                  Save Changes
                </Button>
              </div>
            </Form>
          </div>

          {/* Monetization Settings */}
          <div className={styles.section}>
            <h2>Monetization</h2>
            <Form method="post" className={styles.form}>
              <input type="hidden" name="_section" value="monetization" />
              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="isPremium"
                    value="true"
                    defaultChecked={podcast.isPremium}
                    disabled={isSubmitting}
                  />
                  <span>Enable premium/paid content</span>
                </label>
                <small className={styles.helpText}>
                  Allow paid subscribers to access exclusive episodes
                </small>
              </div>

              {podcast.isPremium && (
                <>
                  <div className={styles.formGroup}>
                    <label htmlFor="revocationPolicy">Revocation Policy</label>
                    <select
                      id="revocationPolicy"
                      name="revocationPolicy"
                      defaultValue={podcast.revocationPolicy}
                      disabled={isSubmitting}
                    >
                      <option value="immediate">
                        Immediate - Remove access instantly on cancellation
                      </option>
                      <option value="end_of_period">
                        End of Period - Access until subscription expires
                      </option>
                      <option value="grace_period">
                        Grace Period - Access for additional days after cancellation
                      </option>
                    </select>
                    <small className={styles.helpText}>
                      What happens when a subscriber cancels their subscription
                    </small>
                  </div>

                  {podcast.revocationPolicy === "grace_period" && (
                    <div className={styles.formGroup}>
                      <label htmlFor="revocationGraceDays">Grace Period (Days)</label>
                      <input
                        id="revocationGraceDays"
                        name="revocationGraceDays"
                        type="number"
                        min="0"
                        max="90"
                        defaultValue={podcast.revocationGraceDays}
                        disabled={isSubmitting}
                      />
                    </div>
                  )}
                </>
              )}

              <div className={styles.formActions}>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting}
                  loadingText="Saving..."
                >
                  Save Monetization Settings
                </Button>
              </div>
            </Form>

            {podcast.isPremium && (
              <PricingManagement organizationId={organizationId} podcastId={podcast.id} />
            )}
          </div>

          {/* Distribution IDs */}
          <div className={styles.section}>
            <h2>Distribution</h2>
            <Form method="post" className={styles.form}>
              <input type="hidden" name="_section" value="distribution" />
              <p className={styles.sectionDescription}>
                Link your podcast to external platforms for tracking and analytics.
              </p>

              <div className={styles.formGroup}>
                <label htmlFor="applePodcastsId">Apple Podcasts ID</label>
                <input
                  id="applePodcastsId"
                  name="applePodcastsId"
                  type="text"
                  defaultValue={podcast.applePodcastsId || ""}
                  placeholder="1234567890"
                  disabled={isSubmitting}
                />
                <small className={styles.helpText}>
                  Found in your Apple Podcasts Connect dashboard
                </small>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="spotifyId">Spotify ID</label>
                <input
                  id="spotifyId"
                  name="spotifyId"
                  type="text"
                  defaultValue={podcast.spotifyId || ""}
                  placeholder="abc123def456"
                  disabled={isSubmitting}
                />
                <small className={styles.helpText}>
                  Found in your Spotify for Podcasters dashboard
                </small>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="youtubeMusicId">YouTube Music ID</label>
                <input
                  id="youtubeMusicId"
                  name="youtubeMusicId"
                  type="text"
                  defaultValue={podcast.youtubeMusicId || ""}
                  placeholder="UCxxxxxxxx"
                  disabled={isSubmitting}
                />
              </div>

              <div className={styles.formActions}>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting}
                  loadingText="Saving..."
                >
                  Save Distribution Settings
                </Button>
              </div>
            </Form>
          </div>

          {/* Custom Domain */}
          <div className={styles.section}>
            <CustomDomainSection organizationId={organizationId} podcastId={podcast.id} />
          </div>

          {/* Team Access */}
          <div className={styles.section}>
            <TeamAccessSection
              organizationId={organizationId}
              podcastId={podcast.id}
              userRole={userRole}
            />
          </div>

          {/* Danger Zone */}
          {canDelete && (
            <div className={`${styles.section} ${styles.dangerZone}`}>
              <h2>Danger Zone</h2>
              <div className={styles.dangerContent}>
                <div>
                  <h3>Delete Podcast</h3>
                  <p>
                    Once you delete a podcast, there is no going back. This will permanently delete
                    the podcast, all episodes, and all subscriber data.
                  </p>
                </div>
                {!showDeleteConfirm ? (
                  <Button type="button" variant="danger" onClick={() => setShowDeleteConfirm(true)}>
                    Delete Podcast
                  </Button>
                ) : (
                  <div className={styles.deleteConfirm}>
                    <p className={styles.confirmText}>
                      Are you sure? This action cannot be undone.
                    </p>
                    <div className={styles.confirmActions}>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={isDeleting}
                      >
                        Cancel
                      </Button>
                      <deleteFetcher.Form
                        method="post"
                        action={`/organizations/${organizationId}/podcasts/${podcast.id}/delete`}
                      >
                        <Button
                          type="submit"
                          variant="danger"
                          loading={isDeleting}
                          loadingText="Deleting..."
                        >
                          Yes, Delete Forever
                        </Button>
                      </deleteFetcher.Form>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
